import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LocationSelector } from '../components/LocationSelector';
import { toast } from 'sonner';
import { User, Store, Truck, Mail, Lock, Phone, ArrowLeft, ShoppingBag, CheckCircle } from 'lucide-react';

const Auth = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'customer',
    location: null
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      
      if (user.role === 'customer') navigate('/customer/dashboard');
      else if (user.role === 'shop_owner') navigate('/shop/dashboard');
      else if (user.role === 'delivery_agent') navigate('/delivery/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // If no location set, use a default location
    const finalData = {
      ...registerData,
      location: registerData.location || { address: 'Default Location', lat: 0, lng: 0 }
    };
    
    setLoading(true);
    try {
      const user = await register(finalData);
      toast.success('Account created successfully!');
      
      if (user.role === 'customer') navigate('/customer/dashboard');
      else if (user.role === 'shop_owner') navigate('/shop/dashboard');
      else if (user.role === 'delivery_agent') navigate('/delivery/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { 
      value: 'customer', 
      label: 'Customer', 
      icon: User, 
      description: 'Order from local shops',
      gradient: 'from-emerald-400 to-emerald-600',
      bgHover: 'hover:bg-emerald-50'
    },
    { 
      value: 'shop_owner', 
      label: 'Shop Owner', 
      icon: Store, 
      description: 'Manage your shop',
      gradient: 'from-orange-400 to-orange-600',
      bgHover: 'hover:bg-orange-50'
    },
    { 
      value: 'delivery_agent', 
      label: 'Delivery Agent', 
      icon: Truck, 
      description: 'Deliver orders',
      gradient: 'from-blue-400 to-blue-600',
      bgHover: 'hover:bg-blue-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-orange-50 flex items-center justify-center p-4">
      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>

        {/* Auth Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                <ShoppingBag className="text-white" size={24} />
              </div>
              <span className="font-bold text-2xl bg-gradient-to-r from-emerald-600 to-orange-500 bg-clip-text text-transparent">
                SamaanDena
              </span>
            </div>
            <h1 className="font-bold text-2xl text-gray-900 mb-2">
              {isLogin ? 'Welcome Back!' : 'Join Our Community'}
            </h1>
            <p className="text-gray-500">
              {isLogin ? 'Login to continue shopping' : 'Create your account to get started'}
            </p>
          </div>

          <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100 p-1 rounded-full">
              <TabsTrigger 
                value="login" 
                data-testid="login-tab"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
              >
                Login
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                data-testid="register-tab"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
              >
                Register
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-gray-700 font-medium">Email</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="login-email"
                      data-testid="login-email"
                      type="email"
                      required
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="pl-12 h-12 bg-gray-50 border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="login-password" className="text-gray-700 font-medium">Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type="password"
                      required
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-12 h-12 bg-gray-50 border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 rounded-xl"
                    />
                  </div>
                </div>

                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 font-semibold text-base"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Logging in...
                    </span>
                  ) : (
                    'Login'
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name" className="text-gray-700 font-medium">Full Name</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="register-name"
                      data-testid="register-name"
                      type="text"
                      required
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      placeholder="John Doe"
                      className="pl-12 h-12 bg-gray-50 border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="register-email" className="text-gray-700 font-medium">Email</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="register-email"
                      data-testid="register-email"
                      type="email"
                      required
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="pl-12 h-12 bg-gray-50 border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="register-phone" className="text-gray-700 font-medium">Phone Number</Label>
                  <div className="relative mt-2">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="register-phone"
                      data-testid="register-phone"
                      type="tel"
                      required
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      placeholder="+1234567890"
                      className="pl-12 h-12 bg-gray-50 border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="register-password" className="text-gray-700 font-medium">Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="register-password"
                      data-testid="register-password"
                      type="password"
                      required
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-12 h-12 bg-gray-50 border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700 font-medium mb-3 block">I am a</Label>
                  <div className="grid grid-cols-1 gap-3">
                    {roleOptions.map((role) => (
                      <button
                        key={role.value}
                        type="button"
                        data-testid={`role-${role.value}`}
                        onClick={() => setRegisterData({ ...registerData, role: role.value })}
                        className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                          registerData.role === role.value
                            ? `border-emerald-400 bg-emerald-50 shadow-md`
                            : `border-gray-200 ${role.bgHover}`
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 bg-gradient-to-br ${role.gradient} rounded-xl flex items-center justify-center text-white shadow-md`}>
                            <role.icon size={20} />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{role.label}</div>
                            <div className="text-sm text-gray-500">{role.description}</div>
                          </div>
                          {registerData.role === role.value && (
                            <CheckCircle className="text-emerald-500" size={20} />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <LocationSelector
                  onLocationSelect={(location) => setRegisterData({ ...registerData, location })}
                />

                <Button
                  data-testid="register-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 font-semibold text-base"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating Account...
                    </span>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Auth;
